import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { WorkspaceScanSummary } from '../../components/screens/RunScreens';
import type { RunIssueAcceptanceStatus } from './appRunData';
import { RunMdBlock, SEVERITY_LABELS } from '../../run/RunIssueDashboard';
import { IconChat, IconCheckCircle, IconClose, IconDismiss, IconFile, IconFix } from './appIcons';
import type { RegisterIssue, RegisterStatus } from './useWorkspaceIssues';

export const ISSUE_STATUS_LABELS: Record<RegisterStatus, string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  fixed: 'Fixed',
  dismissed: 'Dismissed'
};

/** A dot and a label; only critical findings get a filled badge, so they stand out in a long list. */
export function AppSeverity({ severity }: { severity: RegisterIssue['severity'] }) {
  return (
    <span className={`app-sev sev-${severity}`}>
      <span className={`app-sev-dot sev-${severity}`} aria-hidden={true} />
      {SEVERITY_LABELS[severity]}
    </span>
  );
}

export function AppStatusPill({ status }: { status: RegisterStatus }) {
  return status === 'open'
    ? null
    : <span className={`app-status-pill ${status}`}>{ISSUE_STATUS_LABELS[status]}</span>;
}

export const formatScanDate = (scan: WorkspaceScanSummary): string => {
  return new Date(scan.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

interface AppIssuePanelProps {
  issue: RegisterIssue;
  onClose: () => void;
  onSetStatus: (status: RunIssueAcceptanceStatus | null) => Promise<void>;
  onFix: () => void;
  onAsk: () => void;
}

function AppIssueSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="app-panel-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

/** Right-hand view with a finding's full report, in the style of a canvas next to the chat. */
function AppIssuePanel({ issue, onClose, onSetStatus, onFix, onAsk }: AppIssuePanelProps) {
  const [updating, setUpdating] = useState<RunIssueAcceptanceStatus | null>(null);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const review = issue.acceptanceStatus;
  const scanCount = issue.occurrences.length;

  useEffect(() => {
    setError('');
    scrollRef.current?.scrollTo({ top: 0 });
  }, [issue.key]);

  // Clicking the active review status again reopens the finding.
  const toggleStatus = (next: RunIssueAcceptanceStatus) => {
    setUpdating(next);
    setError('');
    onSetStatus(review === next ? null : next)
      .catch((statusError: unknown) => {
        console.error('Failed to update issue status:', statusError);
        setError('The status could not be saved. Please try again.');
      })
      .finally(() => setUpdating(null));
  };

  return (
    <aside className="app-panel" aria-label="Issue report">
      <header className="app-panel-head">
        <AppSeverity severity={issue.severity} />
        <AppStatusPill status={issue.status} />
        <button type="button" className="app-icon-button app-panel-close" aria-label="Close report" title="Close" onClick={onClose}>
          <IconClose aria-hidden={true} />
        </button>
      </header>

      <div className="app-panel-scroll" ref={scrollRef}>
        <h2 className="app-panel-title">{issue.cleanTitle}</h2>
        <p className="app-panel-meta">
          {scanCount === 1
            ? `Found ${formatScanDate(issue.firstSeen)}`
            : `Found in ${scanCount} scans · first ${formatScanDate(issue.firstSeen)} · last ${formatScanDate(issue.lastSeen)}`}
          {issue.lastSeen.branch && ` · ${issue.lastSeen.branch}`}
        </p>

        <div className="app-panel-actions" role="group" aria-label="Issue actions">
          <button
            type="button"
            className={`app-panel-action${review === 'acknowledged' ? ' active' : ''}`}
            aria-pressed={review === 'acknowledged'}
            disabled={updating !== null}
            onClick={() => toggleStatus('acknowledged')}
          >
            {updating === 'acknowledged' ? <span className="app-spinner" aria-hidden={true} /> : <IconCheckCircle aria-hidden={true} />}
            {review === 'acknowledged' ? 'Acknowledged' : 'Acknowledge'}
          </button>
          <button type="button" className="app-panel-action primary" onClick={onFix}>
            <IconFix aria-hidden={true} />
            Fix
          </button>
          <button
            type="button"
            className={`app-panel-action${review === 'accepted' ? ' active' : ''}`}
            aria-pressed={review === 'accepted'}
            disabled={updating !== null}
            onClick={() => toggleStatus('accepted')}
          >
            {updating === 'accepted' ? <span className="app-spinner" aria-hidden={true} /> : <IconDismiss aria-hidden={true} />}
            {review === 'accepted' ? 'Dismissed' : 'Dismiss'}
          </button>
          <button type="button" className="app-panel-action quiet" onClick={onAsk}>
            <IconChat aria-hidden={true} />
            Ask
          </button>
        </div>
        {error && <p className="app-widget-error app-panel-error" role="alert">{error}</p>}

        <div className="app-panel-body">
          {issue.summary && (
            <AppIssueSection title="Summary">
              <RunMdBlock text={issue.summary} />
            </AppIssueSection>
          )}
          {issue.detail && (
            <AppIssueSection title="Detail">
              <RunMdBlock text={issue.detail} />
            </AppIssueSection>
          )}
          {issue.impact && (
            <AppIssueSection title="Impact">
              <RunMdBlock text={issue.impact} />
            </AppIssueSection>
          )}
          {issue.files.length > 0 && (
            <AppIssueSection title="Affected files">
              <div className="app-issue-files">
                {issue.files.map((file) => (
                  <code key={file}>
                    <IconFile aria-hidden={true} />
                    {file}
                  </code>
                ))}
              </div>
            </AppIssueSection>
          )}
          {issue.sources.length > 0 && (
            <AppIssueSection title="Found by">
              <div className="app-panel-tags">
                {issue.sources.map((source) => <span key={source}>{source}</span>)}
              </div>
            </AppIssueSection>
          )}
        </div>
      </div>
    </aside>
  );
}

export default AppIssuePanel;
